/**
 * /parent — 家长只读视图:孩子的班级、作业成绩、考试成绩。
 * 数据来自 /api/school/parent/me(按钉钉家校通讯录的亲属关系限定到自己的孩子)。
 */
import { useTranslation } from "react-i18next";

import { useParentMe, type ParentChild } from "@/api/dingtalk";

function pct(score: number | null, max: number | null): string {
  if (score == null) return "—";
  return max ? `${score} / ${max}` : String(score);
}

export default function ParentPage() {
  const { t } = useTranslation();
  const me = useParentMe();

  if (me.isPending) return <div className="p-6 text-sm text-slate-500">{t("parent.loading")}</div>;
  if (me.error) return <div className="p-6 text-sm text-rose-600">{(me.error as Error).message}</div>;
  const data = me.data!;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <header className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-800">{t("parent.title")}</h1>
        <p className="text-xs text-slate-500 mt-1">{t("parent.hint")}</p>
      </header>
      {!data.is_guardian && <p className="text-sm text-slate-500">{t("parent.notGuardian")}</p>}
      {data.is_guardian && data.children.length === 0 && (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">{t("parent.noChildren")}</p>
      )}
      {!!data.unmatched_children && data.children.length > 0 && (
        <p className="mb-4 text-xs text-amber-700">{t("parent.someUnmatched", { n: data.unmatched_children })}</p>
      )}
      <div className="flex flex-col gap-5">
        {data.children.map((c) => (
          <ChildCard key={c.id} child={c} />
        ))}
      </div>
    </div>
  );
}

function ChildCard({ child }: { child: ParentChild }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid="parent-child">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-slate-100 px-4 py-3">
        <h2 className="text-lg font-semibold text-slate-800">{child.name}</h2>
        {child.name_en && <span className="text-sm text-slate-500">{child.name_en}</span>}
        {child.relation && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{child.relation}</span>}
        <span className="text-xs text-slate-500">{child.classes.join("、")}</span>
      </div>

      {!!child.feedback?.length && (
        <div className="border-b border-slate-100 px-4 py-3">
          <h3 className="mb-2 text-sm font-medium text-slate-700">{t("parent.feedback")}</h3>
          <div className="flex flex-col gap-3">
            {child.feedback.map((f, i) => (
              <div key={i} className={i === 0 ? "" : "border-t border-slate-100 pt-3"}>
                <div className="mb-1 text-xs text-slate-400">{f.period_start} – {f.period_end}{f.teacher_name ? ` · ${f.teacher_name}` : ""}</div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{f.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(child.conduct || child.attendance) && (
        <div className="grid gap-4 border-b border-slate-100 px-4 py-3 sm:grid-cols-2">
          {child.conduct && (
            <div>
              <h3 className="mb-1 text-sm font-medium text-slate-700">{t("parent.conduct")}</h3>
              <p className="text-2xl font-semibold tabular-nums text-slate-800">{child.conduct.total}<span className="ml-1 text-xs font-normal text-slate-400">{t("parent.conductBase", { n: child.conduct.base_score })}</span></p>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                {child.conduct.records.slice(0, 5).map((r, i) => (
                  <li key={i}>
                    <span className="text-slate-400">{r.recorded_on}</span> {r.category}{r.item ? ` · ${r.item}` : ""}{" "}
                    <b className={r.points >= 0 ? "text-emerald-700" : "text-rose-600"}>{r.points > 0 ? `+${r.points}` : r.points}</b>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {child.attendance && (
            <div>
              <h3 className="mb-1 text-sm font-medium text-slate-700">{t("parent.attendance", { n: child.attendance.days })}</h3>
              {child.attendance.lessons === 0 ? (
                <p className="text-xs text-slate-400">{t("parent.noAttendance")}</p>
              ) : (
                <>
                  <p className="text-xs text-slate-600">
                    {t("parent.attendanceCounts", {
                      n: child.attendance.lessons,
                      late: child.attendance.counts.late ?? 0,
                      absent: child.attendance.counts.absent ?? 0,
                      leave: child.attendance.counts.leave ?? 0,
                    })}
                  </p>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                    {child.attendance.absences.slice(0, 5).map((a, i) => <li key={i}>{a.date} {a.period} · {a.status}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {child.semester_grades && (child.semester_grades.courses.length > 0 || child.semester_grades.gpa_pending) && (
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
            <h3 className="text-sm font-medium text-slate-700">{t("parent.semesterGrades")}</h3>
            {child.semester_grades.gpa != null ? (
              <span className="text-sm text-slate-700">GPA <b className="tabular-nums">{child.semester_grades.gpa}</b>
                {child.semester_grades.gpa_adjusted != null && child.semester_grades.gpa_adjusted !== child.semester_grades.gpa && (
                  <span className="ml-1 text-xs text-slate-500">{t("parent.gpaAdjusted", { n: child.semester_grades.gpa_adjusted })}</span>
                )}
              </span>
            ) : child.semester_grades.gpa_pending ? (
              <span className="text-xs text-slate-400">{t("parent.gpaPending")}</span>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            {child.semester_grades.courses.map((c, i) => (
              <details key={i} className="rounded border border-slate-100 px-3 py-2">
                <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-3 text-sm">
                  <span className="font-medium text-slate-800">{c.name}</span>
                  <span className="tabular-nums text-slate-700">{c.final_score ?? "—"}{c.total_score !== 100 ? ` / ${c.total_score}` : ""}</span>
                  {c.gpa_label && <span className="text-xs text-sky-700">{c.gpa_label}</span>}
                  {c.domestic_label && <span className="text-xs text-emerald-700">{c.domestic_label}</span>}
                </summary>
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  {c.items.map((it, j) => (
                    <li key={j}>
                      <span className="text-slate-700">{it.label}</span> <span className="text-slate-400">{it.weight}%</span>{" "}
                      <b className="tabular-nums">{it.average ?? "—"}</b>
                      {it.entries.length > 0 && (
                        <span className="ml-1 text-slate-400">({it.entries.map((e) => `${e.label} ${e.score}/${e.max}`).join(";")})</span>
                      )}
                    </li>
                  ))}
                </ul>
                {c.comment && <p className="mt-2 text-xs text-slate-600">{t("parent.note")}{c.comment}</p>}
              </details>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <h3 className="mb-2 text-sm font-medium text-slate-700">{t("parent.homework")}</h3>
        {child.homework.length === 0 ? (
          <p className="text-xs text-slate-400">{t("parent.noHomework")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {child.homework.map((h, i) => (
                  <tr key={i} className="border-t border-slate-100 first:border-t-0 align-top">
                    <td className="py-2 pr-3">
                      <div className="text-slate-800">{h.title}</div>
                      <div className="text-xs text-slate-400">
                        {h.class_name} · {h.assigned_date}
                      </div>
                      {h.teacher_note && <div className="mt-1 text-xs text-slate-600">{t("parent.note")}{h.teacher_note}</div>}
                    </td>
                    <td className="py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">{pct(h.score, h.max_score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 px-4 py-3">
        <h3 className="mb-2 text-sm font-medium text-slate-700">{t("parent.exams")}</h3>
        {child.exams.length === 0 ? (
          <p className="text-xs text-slate-400">{t("parent.noExams")}</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {child.exams.map((e, i) => (
                <tr key={i} className="border-t border-slate-100 first:border-t-0">
                  <td className="py-2 pr-3">
                    <div className="text-slate-800">{e.title}</div>
                    <div className="text-xs text-slate-400">{e.exam_date}</div>
                  </td>
                  <td className="py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">{pct(e.total, e.max_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
